
import moment from 'moment';
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  CancelledConnection, Connection, ProgressDetails as ProgressDetailsType,
} from '@/types';
import ProgressDetails from '@/components/ProgressDetails.vue';
import AttachmentStatusLabel from '@/components/AttachmentStatusLabel.vue';

moment.locale('it');

@Component({
  components: {
    ProgressDetails,
    AttachmentStatusLabel,
  },
})
export default class CancelledConnectionDetailsDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop(Object) readonly cancelledConnection: CancelledConnection | null | undefined;

  dialog = false

  get snapshot(): Connection | null {
    return this.cancelledConnection?.snapshot
      ? JSON.parse(this.cancelledConnection.snapshot)
      : null;
  }

  get mup1Details(): ProgressDetailsType {
    return (this.snapshot as any)?.details?.mup1 ?? {};
  }

  get mup2Details(): ProgressDetailsType {
    return (this.snapshot as any)?.details?.mup2 ?? {};
  }

  get verbaleAllaccioDetails(): string {
    return this.snapshot?.verbaleAllaccio
      ? this.snapshot.verbaleAllaccio
      : 'na';
  }

  get allacciamentoDetails(): any {
    return (this.snapshot as any)?.details?.allacciamento ?? {};
  }

  get egridConnectionStatusValue(): string | null {
    return this.allacciamentoDetails?.egridConnectionStatus?.value ?? null;
  }

  get egridConnectionStatusLabel(): string {
    return this.allacciamentoDetails?.egridConnectionStatus?.statusLabel ?? '-';
  }

  get showAgreedDate(): boolean {
    const s = this.egridConnectionStatusValue;
    return s === 'not-confirmed' || s === 'no-confirmation-required' || s === 'confirmed' || s === 'connected';
  }

  get showSuggestedDates(): boolean {
    return this.egridConnectionStatusValue === 'suggested';
  }

  get showActiveDate(): boolean {
    return this.egridConnectionStatusValue === 'connected';
  }

  get egridConnectionDateTime(): string {
    const date = this.allacciamentoDetails?.egridConnectionDate;
    const time = this.allacciamentoDetails?.egridConnectionTime;
    if (!date && !time) return '-';
    const dateLabel = date ? moment(date).format('L') : '-';
    return time ? `${dateLabel} (${time})` : dateLabel;
  }

  get suggestedEgridConnectionDateTime1(): string {
    const date = this.allacciamentoDetails?.suggestedEgridConnectionDate1;
    const time = this.allacciamentoDetails?.suggestedEgridConnectionTime1;
    if (!date && !time) return '-';
    const dateLabel = date ? moment(date).format('L') : '-';
    return time ? `${dateLabel} (${time})` : dateLabel;
  }

  get suggestedEgridConnectionDateTime2(): string {
    const date = this.allacciamentoDetails?.suggestedEgridConnectionDate2;
    const time = this.allacciamentoDetails?.suggestedEgridConnectionTime2;
    if (!date && !time) return '-';
    const dateLabel = date ? moment(date).format('L') : '-';
    return time ? `${dateLabel} (${time})` : dateLabel;
  }

  get egridConnectionActiveDate(): string {
    const date = this.allacciamentoDetails?.egridConnectionActiveDate;
    return date ? moment(date).format('L') : '-';
  }

  get egridDistributorAddress(): string {
    return this.allacciamentoDetails?.egridDistributorAddress ?? '-';
  }

  get egridConnectionNotes(): string {
    return this.allacciamentoDetails?.egridConnectionNotes ?? '-';
  }

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : '-'; }

  @Watch('value')
  async onValueChange(val: boolean) {
    this.dialog = val;
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  get cancelDate(): string {
    return this.cancelledConnection?.cancelDate
      ? moment(this.cancelledConnection.cancelDate).format('L')
      : '-';
  }

  get reason(): string {
    return this.cancelledConnection?.reason
      ? this.cancelledConnection.reason
      : '-';
  }

  get practiceAssignee(): string {
    let result = `
      Assegnatario: ${this.snapshot?.practiceAssigneeName ? this.snapshot.practiceAssigneeName : '-'}<br>
      Data assegnazione: ${this.snapshot?.practiceAssignedDate ? moment(this.snapshot.practiceAssignedDate).format('L') : '-'}<br>
    `;

    // this.snapshot.practiceAssigned potrebbe essere booleano o stringa
    // Prima di fare il controllo, verifica il tipo
    if (typeof this.snapshot?.practiceAssigned === 'string') {
      if (this.snapshot.practiceAssigned === 'done') {
        result += 'Assegnazione confermata';
      } else if (this.snapshot.practiceAssigned === 'todo') {
        result += 'Assegnazione non confermata';
      }
    } else if (typeof this.snapshot?.practiceAssigned === 'boolean') {
      if (this.snapshot.practiceAssigned) {
        result += 'Assegnazione confermata';
      } else {
        result += 'Assegnazione non confermata';
      }
    }

    return result;
  }

  get mup1Progress(): string {
    const progress = this.snapshot?.mup1 ?? 0;

    return `Progresso: ${Math.ceil(progress * 100)}%`;
  }

  get mup2Progress(): string {
    const progress = this.snapshot?.mup2 ?? 0;

    return `Progresso: ${Math.ceil(progress * 100)}%`;
  }

  get practiceNumber(): string {
    return this.snapshot?.practiceNumber
      ? this.snapshot.practiceNumber
      : '-';
  }

  get mup1RequestDate(): string {
    return this.snapshot?.mup1RequestDate
      ? moment(this.snapshot.mup1RequestDate).format('L')
      : '-';
  }

  get practiceSendingDate(): string {
    return this.snapshot?.practiceSendingDate
      ? moment(this.snapshot.practiceSendingDate).format('L')
      : '-';
  }

  get practiceSendingAnagraficaDelay(): string {
    return this.snapshot?.practiceSendingAnagraficaDelay ? 'Sì' : 'No';
  }

  get endOfWorksDate(): string {
    return this.snapshot?.endOfWorksDate
      ? moment(this.snapshot.endOfWorksDate).format('L')
      : '-';
  }

  get rdeSendingDate(): string {
    return this.snapshot?.rdeSendingDate
      ? moment(this.snapshot.rdeSendingDate).format('L')
      : '-';
  }

  get iter(): string {
    if (this.snapshot?.iter === 'o') return 'Ordinario';
    if (this.snapshot?.iter === 's') return 'Semplificato';
    if (this.snapshot?.iter === 'bo') return 'Superbonus 110% - Ordinario';
    if (this.snapshot?.iter === 'bs') return 'Superbonus 110% - Semplificato';
    if (this.snapshot?.iter === 'b') return 'Superbonus 110%';
    if (this.snapshot?.iter === 'ps') return 'Pre Check Connessione';
    if (this.snapshot?.iter === 'n') return 'Non applicabile';
    return 'Non selezionato';
  }

  onCancel() {
    this.dialog = false;
  }
}

